import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { immer } from 'zustand/middleware/immer';
import type { SchemaField, CompatibilityMode } from '../types/schema.types';

/**
 * Schema creation wizard steps
 *
 * The wizard has 3 steps:
 * 1. UPLOAD (0) - Upload sample file for schema inference
 * 2. FIELD_EDITOR (1) - Review and edit inferred fields
 * 3. METADATA (2) - Provide schema name and settings
 */
export enum SchemaCreationStep {
  UPLOAD = 0,
  FIELD_EDITOR = 1,
  METADATA = 2,
}

/**
 * Schema creation wizard state
 *
 * Manages multi-step schema creation flow with file upload,
 * inference, field editing, and metadata configuration.
 */
interface SchemaCreationState {
  // Current wizard state
  currentStep: SchemaCreationStep;
  isComplete: boolean;

  // File upload step
  uploadedFile: File | null;
  fileFormat: string | null;

  // Inference step
  inferredFields: SchemaField[];
  rowCount: number;
  detectedPII: string[];

  // Field editor step
  fields: SchemaField[];

  // Metadata step
  schemaName: string;
  compatibilityMode: CompatibilityMode;
  description: string;

  // Validation state
  errors: Record<string, string>;
}

/**
 * Schema creation wizard actions
 */
interface SchemaCreationActions {
  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: SchemaCreationStep) => void;
  resetWizard: () => void;

  // File upload
  setUploadedFile: (file: File | null) => void;

  // Inference results
  setInferenceResults: (params: {
    fields: SchemaField[];
    fileFormat: string;
    rowCount: number;
    detectedPII: string[];
  }) => void;

  // Field management
  setFields: (fields: SchemaField[]) => void;
  addField: (field: SchemaField) => void;
  updateField: (index: number, field: Partial<SchemaField>) => void;
  removeField: (index: number) => void;
  reorderFields: (startIndex: number, endIndex: number) => void;

  // Metadata
  setSchemaName: (name: string) => void;
  setCompatibilityMode: (mode: CompatibilityMode) => void;
  setDescription: (description: string) => void;

  // Validation
  setError: (field: string, error: string) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;

  // Completion
  markComplete: () => void;
}

type SchemaCreationStore = SchemaCreationState & SchemaCreationActions;

const TOTAL_STEPS = 3;

/**
 * Initial state for the schema creation wizard
 */
const initialState: SchemaCreationState = {
  currentStep: SchemaCreationStep.UPLOAD,
  isComplete: false,
  uploadedFile: null,
  fileFormat: null,
  inferredFields: [],
  rowCount: 0,
  detectedPII: [],
  fields: [],
  schemaName: '',
  compatibilityMode: 'backward',
  description: '',
  errors: {},
};

/**
 * Zustand store for schema creation wizard
 *
 * Manages all state for the multi-step schema creation process,
 * including file upload, inference, field editing, and metadata.
 *
 * Uses Immer middleware for immutable state updates.
 *
 * @example
 * ```tsx
 * function SchemaCreationWizard() {
 *   const store = useSchemaCreationStore();
 *
 *   return (
 *     <div>
 *       {store.currentStep === SchemaCreationStep.UPLOAD && (
 *         <FileUploadStep />
 *       )}
 *       {store.currentStep === SchemaCreationStep.FIELD_EDITOR && (
 *         <FieldEditorStep />
 *       )}
 *       <WizardNavigation
 *         onNext={store.nextStep}
 *         onPrevious={store.previousStep}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export const useSchemaCreationStore = create<SchemaCreationStore>()(
  immer((set) => ({
    ...initialState,

    // Navigation actions
    nextStep: () =>
      set((state) => {
        if (state.currentStep < TOTAL_STEPS - 1) {
          state.currentStep += 1;
        }
      }),

    previousStep: () =>
      set((state) => {
        if (state.currentStep > 0) {
          state.currentStep -= 1;
        }
      }),

    goToStep: (step: SchemaCreationStep) =>
      set((state) => {
        state.currentStep = step;
      }),

    resetWizard: () =>
      set((state) => {
        // Create fresh copies to avoid shared references
        state.currentStep = SchemaCreationStep.UPLOAD;
        state.isComplete = false;
        state.uploadedFile = null;
        state.fileFormat = null;
        state.inferredFields = [];
        state.rowCount = 0;
        state.detectedPII = [];
        state.fields = [];
        state.schemaName = '';
        state.compatibilityMode = 'backward';
        state.description = '';
        state.errors = {};
      }),

    // File upload actions
    setUploadedFile: (file: File | null) =>
      set((state) => {
        state.uploadedFile = file;
      }),

    // Inference results
    setInferenceResults: ({ fields, fileFormat, rowCount, detectedPII }) =>
      set((state) => {
        state.inferredFields = fields;
        state.fields = fields; // Copy to editable fields
        state.fileFormat = fileFormat;
        state.rowCount = rowCount;
        state.detectedPII = detectedPII;
      }),

    // Field management actions
    setFields: (fields: SchemaField[]) =>
      set((state) => {
        state.fields = fields;
      }),

    addField: (field: SchemaField) =>
      set((state) => {
        state.fields.push(field);
      }),

    updateField: (index: number, fieldUpdate: Partial<SchemaField>) =>
      set((state) => {
        if (index >= 0 && index < state.fields.length) {
          state.fields[index] = {
            ...state.fields[index],
            ...fieldUpdate,
          };
        }
      }),

    removeField: (index: number) =>
      set((state) => {
        if (index >= 0 && index < state.fields.length) {
          state.fields.splice(index, 1);
        }
      }),

    reorderFields: (startIndex: number, endIndex: number) =>
      set((state) => {
        if (
          startIndex >= 0 &&
          startIndex < state.fields.length &&
          endIndex >= 0 &&
          endIndex < state.fields.length
        ) {
          const [removed] = state.fields.splice(startIndex, 1);
          state.fields.splice(endIndex, 0, removed);
        }
      }),

    // Metadata actions
    setSchemaName: (name: string) =>
      set((state) => {
        state.schemaName = name;
      }),

    setCompatibilityMode: (mode: CompatibilityMode) =>
      set((state) => {
        state.compatibilityMode = mode;
      }),

    setDescription: (description: string) =>
      set((state) => {
        state.description = description;
      }),

    // Validation actions
    setError: (field: string, error: string) =>
      set((state) => {
        state.errors[field] = error;
      }),

    clearError: (field: string) =>
      set((state) => {
        delete state.errors[field];
      }),

    clearAllErrors: () =>
      set((state) => {
        state.errors = {};
      }),

    // Completion
    markComplete: () =>
      set((state) => {
        state.isComplete = true;
      }),
  }))
);

/**
 * Selector hooks for optimized re-renders
 */

/**
 * Get current wizard step
 */
export const useCurrentStep = () =>
  useSchemaCreationStore((state) => state.currentStep);

/**
 * Get navigation actions
 */
export const useWizardNavigation = () =>
  useSchemaCreationStore(
    useShallow((state) => ({
      nextStep: state.nextStep,
      previousStep: state.previousStep,
      goToStep: state.goToStep,
      currentStep: state.currentStep,
    }))
  );

/**
 * Get field editor state and actions
 */
export const useFieldEditor = () =>
  useSchemaCreationStore(
    useShallow((state) => ({
      fields: state.fields,
      setFields: state.setFields,
      addField: state.addField,
      updateField: state.updateField,
      removeField: state.removeField,
      reorderFields: state.reorderFields,
    }))
  );

/**
 * Get metadata state and actions
 */
export const useSchemaMetadata = () =>
  useSchemaCreationStore(
    useShallow((state) => ({
      schemaName: state.schemaName,
      compatibilityMode: state.compatibilityMode,
      description: state.description,
      setSchemaName: state.setSchemaName,
      setCompatibilityMode: state.setCompatibilityMode,
      setDescription: state.setDescription,
    }))
  );

/**
 * Get validation state
 */
export const useValidationErrors = () =>
  useSchemaCreationStore(
    useShallow((state) => ({
      errors: state.errors,
      setError: state.setError,
      clearError: state.clearError,
      clearAllErrors: state.clearAllErrors,
      hasErrors: Object.keys(state.errors).length > 0,
    }))
  );

/**
 * Get complete schema creation data for submission
 */
export const useSchemaCreationData = () =>
  useSchemaCreationStore(
    useShallow((state) => ({
      name: state.schemaName,
      compatibility_mode: state.compatibilityMode,
      fields: state.fields,
      description: state.description,
    }))
  );
